import { useLayoutEffect, useRef } from 'react';

const CAPTURE_PATH = '/trpc/non-member-screen';
const CONTEXT_FRAGMENT_KEY = 'analytics-context';
const CAPABILITY_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export type NonMemberScreenAction = 'displayed' | 'joinClicked' | 'logoutClicked';

export interface NonMemberScreenCapture {
  captureJoinClicked(): void;
  captureLogoutClicked(): void;
}

function capabilityFromCurrentFragment(): string | null {
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  const capabilities = fragment.getAll(CONTEXT_FRAGMENT_KEY);

  if (capabilities.length === 0) {
    return null;
  }

  // The bearer is deliberately transient. Remove it even when it is malformed
  // or duplicated, so an untrusted fragment is never retained in browser history.
  fragment.delete(CONTEXT_FRAGMENT_KEY);
  const remainingFragment = fragment.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${window.location.search}${remainingFragment === '' ? '' : `#${remainingFragment}`}`,
  );

  const [capability] = capabilities;
  return capabilities.length === 1 &&
    capability !== undefined &&
    CAPABILITY_PATTERN.test(capability)
    ? capability
    : null;
}

function sendCapture(capability: string, action: NonMemberScreenAction): void {
  try {
    // This is an anonymous, best-effort measurement. Omitting credentials and
    // referrer data keeps it independent of the terminal route's auth flow.
    void fetch(CAPTURE_PATH, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ capability, action }),
      cache: 'no-store',
      credentials: 'omit',
      keepalive: true,
      referrerPolicy: 'no-referrer',
    }).catch(() => {
      // Measurement failure is intentionally not surfaced or logged: the
      // request contains a short-lived bearer and must not affect the action.
    });
  } catch {
    // A synchronous browser transport failure is equally non-blocking.
  }
}

/**
 * Consumes a server-issued non-member screen capability after commit and keeps
 * it only in this mounted hook. Direct public visits have no capability and
 * therefore remain functional but unmeasured.
 */
export function useNonMemberScreenCapture(): NonMemberScreenCapture {
  const capabilityRef = useRef<string | null>(null);
  const displayStartedRef = useRef(false);

  // Browser history is an external system. Layout timing removes the bearer
  // after the screen commits but before a person can interact with it.
  useLayoutEffect(() => {
    if (displayStartedRef.current) {
      return;
    }
    displayStartedRef.current = true;
    const capability = capabilityFromCurrentFragment();
    capabilityRef.current = capability;
    if (capability !== null) {
      sendCapture(capability, 'displayed');
    }
  }, []);

  const captureAction = (action: Exclude<NonMemberScreenAction, 'displayed'>): void => {
    const capability = capabilityRef.current;
    if (capability !== null) {
      sendCapture(capability, action);
    }
  };

  return {
    captureJoinClicked: (): void => captureAction('joinClicked'),
    captureLogoutClicked: (): void => captureAction('logoutClicked'),
  };
}
